import { useMemo, useRef } from 'react';
import { DropzoneWithErrors } from '@ff-os/shared-react/components/file-uploader/DropzoneWithErrors';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { createUploadMethod, UploadMethodsProps } from '@ff-os/shared-react/components/upload-methods/CreateUploadMethod';
import { maximumImageSize } from '@ff-os/shared-utils/constants/maximum-file-sizes';
import { isEqual, noop } from 'lodash';
import { PhotoPrintsDeviceUpload } from './components/PhotoPrintDeviceUploadWindow';

const useDeepCompareMemoize = (value: any) => {
    const ref = useRef(value);
    if (!isEqual(value, ref.current)) {
        ref.current = value;
    }
    return ref.current;
};

export const usePhotoPrintsUploadMethods = (props: PhotoPrintProps) => {
    const memoized = useDeepCompareMemoize(props);
    return useMemo(() => {
        return createPhotoPrintsUploadMethods(memoized);
    }, [memoized]);
};

export const createPhotoPrintsUploadMethods = (
    { dropzone, externalUpload }: PhotoPrintProps
): UploadMethodsProps => {
    return {
        Dropzone: createUploadMethod(DropzoneWithErrors, {
            maxSize: maximumImageSize,
            accept: 'image/png, image/jpeg, image/bmp, image/heic, image/heif',
            onChange: dropzone.onChange,
            onError: dropzone.onError,
            multiple: true,
            closeOnUpload: true
        }),
        ExternalUpload: createUploadMethod(PhotoPrintsDeviceUpload, {
            maxSize: maximumImageSize,
            accept: 'image/png, image/jpeg, image/bmp, image/heic, image/heif',
            onChange: externalUpload.onChange ?? noop,
            multiple: true,
            onOpen: externalUpload.onOpen,
            closeOnUpload: externalUpload.closeOnUpload ?? false,
            groupId: externalUpload.groupId
        })
    };
};

type PhotoPrintProps = {
    dropzone: { onChange: (files: File[]) => void; onError: (error: UploadError[]) => void };
    externalUpload: { groupId: string; closeOnUpload?: boolean; onOpen?: () => void; onChange?: () => void };
};